import { Helmet } from 'react-helmet-async';
import { useMemo, useState } from 'react';
import { Header } from '../landing/components/Header';
import { Footer } from '../landing/components/Footer';
import { Button } from '@/components';
import { buildWhatsAppUrl } from '@/constants/whatsapp';

const GIF_QUESTIONARIO = 'https://i.ibb.co/6c449FLj/questionario.gif';
const GIF_DASHBOARD = 'https://i.ibb.co/DPWszN3z/dashboard.gif';
const GIF_AGENDAR = 'https://i.ibb.co/kVTN7Jm4/agendar.gif';

const CALC_TIERS = [
	{ max: 10, price: 15.0, label: 'Micro', min: 80 },
	{ max: 30, price: 12.0, label: 'Pequeno', min: 0 },
	{ max: 100, price: 8.0, label: 'Médio', min: 0 },
	{ max: Infinity, price: 5.0, label: 'Grande', min: 0 },
] as const;

function getTier(n: number) {
	for (let i = 0; i < CALC_TIERS.length; i++) {
		if (n <= CALC_TIERS[i].max) return { t: CALC_TIERS[i], i };
	}
	return { t: CALC_TIERS[CALC_TIERS.length - 1], i: CALC_TIERS.length - 1 };
}

function brl(n: number, dec?: boolean) {
	if (dec) return `R$ ${n.toFixed(2).replace('.', ',')}`;
	return `R$ ${Math.round(n).toLocaleString('pt-BR')}`;
}

const PAIN_CARDS = [
	{
		quote: '“Não tenho banda pra rodar isso todo mês.”',
		desc: 'Folha, recrutamento, treinamentos, benefícios, clima. O calendário do RH já está cheio — adicionar uma rotina mensal de pesquisa, compilação e laudo é um bloqueio real.',
	},
	{
		quote: '“Faltam as ferramentas certas pra isso.”',
		desc: 'Aplicar formulário científico, compilar análise por setor, gerar laudo técnico e arquivar evidências exige sistema dedicado. Não é algo que se monta na planilha do dia a dia.',
	},
	{
		quote: '“Se a fiscalização chegar, sou eu na linha de frente.”',
		desc: 'Quem entrega a documentação ao auditor é o RH. Sem dados periódicos, plano de ação e respaldo técnico, a multa cai sobre a empresa — e a responsabilidade segue o caminho da assinatura.',
	},
];

const DELIVERABLES = [
	{
		num: '01',
		title: 'Formulários mensais automáticos',
		desc: 'Disparados pela plataforma para 100% da equipe, todo mês, sem precisar de iniciativa do RH.',
	},
	{
		num: '02',
		title: 'Dashboard analítico em tempo real',
		desc: 'Índice geral, análise por setor, alertas de risco e evolução mês a mês — atualizado automaticamente.',
	},
	{
		num: '03',
		title: 'Assinatura médica para o inventário',
		desc: 'Assinatura médica oficial, com CRM, anexável ao inventário NR-1 da sua empresa. Uma vez por contrato, com respaldo legal.',
	},
	{
		num: '04',
		title: '2 palestras de intervenção/mês',
		desc: 'Direcionadas pelos dados reais da equipe. O RH só divulga o link interno.',
	},
	{
		num: '05',
		title: 'Plataforma individual para cada colaborador',
		desc: 'Acesso online a psicólogo, médico e nutricionista — detalhado no bônus, ao final desta página.',
	},
	{
		num: '06',
		title: 'Histórico para auditoria',
		desc: 'Todos os relatórios e evidências arquivados, exportáveis em PDF a qualquer momento.',
	},
];

const BONUS_PLANS = [
	{
		name: 'Lite',
		dot: 'lite' as const,
		tagline: 'Desbloqueie suas métricas de Bem-estar completas.',
		featured: false,
		benefits: [
			'Acesso ao nutricionista com 60% OFF: de R$ 219,90 por apenas R$ 87,96 a sessão.',
			'Acesso a terapia com 60% OFF: de R$ 219,90 por apenas R$ 87,96 a sessão.',
			'Acesso a consulta médica em saúde mental com 70% OFF: de R$ 499,99 por apenas R$ 149,99 a consulta.',
		],
		price: 'R$ 9,97',
	},
	{
		name: 'Go',
		dot: 'go' as const,
		tagline: 'Acesso a terapia grátis todos os meses.',
		featured: false,
		benefits: [
			'Sessão de psicólogo GRÁTIS.',
			'Acesso ao nutricionista com 60% OFF: de R$ 219,90 por apenas R$ 87,96 a sessão.',
			'Acesso a terapia com 60% OFF: de R$ 219,90 por apenas R$ 87,96 a sessão.',
			'Acesso a consulta médica em saúde mental com 70% OFF: de R$ 499,99 por apenas R$ 149,99 a consulta.',
		],
		price: 'R$ 49,97',
	},
	{
		name: 'Standard',
		dot: 'std' as const,
		tagline: 'Tudo do Go com mais sessões de psicólogo online.',
		featured: true,
		benefits: [
			'2 sessões de psicólogo GRÁTIS.',
			'Acesso ao nutricionista com 60% OFF: de R$ 219,90 por apenas R$ 87,96 a sessão.',
			'Acesso a terapia com 60% OFF: de R$ 219,90 por apenas R$ 87,96 a sessão.',
			'Acesso a consulta médica em saúde mental com 70% OFF: de R$ 499,99 por apenas R$ 149,99 a consulta.',
		],
		price: 'R$ 169,97',
	},
	{
		name: 'Premium',
		dot: 'prem' as const,
		tagline: 'Tudo do Standard com mais sessões de psicólogo online.',
		featured: false,
		benefits: [
			'4 sessões de psicólogo GRÁTIS.',
			'App de academia e dietas ilimitado.',
			'Acesso ao nutricionista com 60% OFF: de R$ 219,90 por apenas R$ 87,96 a sessão.',
			'Acesso a terapia com 60% OFF: de R$ 219,90 por apenas R$ 87,96 a sessão.',
			'Acesso a consulta médica em saúde mental com 70% OFF: de R$ 499,99 por apenas R$ 149,99 a consulta.',
		],
		price: 'R$ 197,99',
	},
];

const planDotClass: Record<(typeof BONUS_PLANS)[number]['dot'], string> = {
	lite: 'bg-[#5a5a5a]',
	go: 'bg-[#00b14f]',
	std: 'bg-[#6b46e1]',
	prem: 'bg-[#ff8c00]',
};

export const PropostaGeralPage = () => {
	const [cols, setCols] = useState(20);

	const clampedCols = Math.max(1, Math.min(6000, cols || 1));
	const { t: tier, i: tierIdx } = getTier(clampedCols);
	const monthly = Math.max(clampedCols * tier.price, tier.min ?? 0);
	const annual = monthly * 12;

	const ctaUrl = useMemo(
		() => buildWhatsAppUrl('Oi, quero entender o programa NR-1 da Plumaa'),
		[],
	);

	const handleContact = () => {
		window.open(ctaUrl, '_blank');
	};

	return (
		<section className="flex w-full flex-col items-center bg-brand-cream text-[#1c1c1c]">
			<Helmet>
				<title>Plumaa · Sua NR-1 roda sozinha</title>
				<meta
					name="description"
					content="A Plumaa faz a NR-1 pelo seu RH. Pesquisas, análise, laudo médico e palestras — entregues prontos, todo mês."
				/>
			</Helmet>

			<Header />

			<main className="w-full">
				<section className="relative overflow-hidden bg-[#0c0c0c] px-6 py-14 text-white md:px-10 md:py-16">
					<div className="pointer-events-none absolute right-[-100px] top-[-160px] h-[600px] w-[600px] bg-[radial-gradient(circle,rgba(225,29,116,0.28)_0%,transparent_60%)]" />
					<div className="relative z-10 mx-auto w-full max-w-[800px]">
						<div className="mb-10 flex w-full flex-wrap items-center justify-end gap-3 md:mb-16">
							<span className="font-albert inline-flex rounded-full border border-brand-pink/35 bg-brand-pink/15 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#ff8aaa]">
								Plumaa para empresas · Abril 2026
							</span>
						</div>
						<div className="font-albert mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-[#ff8aaa]">
							NR-1 sem trabalho extra para o RH
						</div>
						<h1 className="font-libre mb-6 text-[32px] font-bold leading-[1.1] tracking-[-0.01em] md:text-[58px]">
							<span className="inline-block">Sua NR-1 roda</span>
							<em className="font-libre inline text-brand-pink">
								{' '}
								sozinha.
							</em>
							<span className="block">
								O RH só liga e acompanha.
							</span>
						</h1>
						<p className="font-albert mb-11 max-w-[540px] text-[17px] font-light leading-[1.75] text-white/60">
							A Plumaa faz a NR-1 pelo seu RH. Pesquisas, análise,
							laudo médico e palestras — entregues prontos, todo
							mês.
						</p>
						<div className="font-albert inline-flex max-w-full flex-col gap-1.5 rounded-lg border border-white/10 bg-white/[0.06] px-5 py-3.5 text-sm text-white/55 sm:flex-row sm:items-center sm:gap-3">
							<strong className="font-semibold text-[#ffd166]">
								26/05/2026
							</strong>
							<span>
								— Fiscalização ativa. Sem programa rodando, sua
								empresa fica exposta.
							</span>
						</div>
					</div>
				</section>

				<section className="bg-white px-6 py-16 md:px-10 md:py-[72px]">
					<div className="mx-auto max-w-[800px]">
						<div className="font-albert mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-pink">
							O que mudou na lei
						</div>
						<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.12] tracking-[-0.015em] text-[#1c1c1c] md:text-[42px]">
							<span className="inline-block">
								A NR-1 agora exige
							</span>
							<em className="font-libre text-brand-pink">
								{' '}
								prova,
							</em>{' '}
							não promessa.
						</h2>
						<p className="font-albert mb-11 max-w-[580px] text-base leading-[1.75] text-[#696969]">
							A atualização da norma transformou saúde mental no
							trabalho de &quot;boa prática&quot; em obrigação
							rastreável — com periodicidade, evidência e plano de
							ação.
						</p>

						<div className="mb-6 rounded-[10px] border-l-[3px] border-brand-pink bg-[#0c0c0c] px-7 py-7 md:px-8">
							<div className="font-albert mb-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#ff8aaa]">
								NR-1 · Vigência 26/05/2026
							</div>
							<p className="font-albert text-[14.5px] leading-[1.75] text-white/80">
								Toda empresa com colaboradores CLT precisa{' '}
								<strong className="font-semibold text-[#ffd166]">
									monitorar, medir e comprovar
								</strong>{' '}
								ações contra riscos psicossociais. Não basta ter
								política no papel — é preciso entregar dados
								periódicos e plano de ação rastreável.
							</p>
						</div>
						<div className="md:py-5.5 rounded-[10px] bg-brand-cream px-6 py-5 md:px-7">
							<p className="font-albert text-sm leading-[1.7] text-[#696969]">
								A fiscalização do Ministério do Trabalho começa
								em{' '}
								<strong className="text-[#1c1c1c]">
									26/05/2026
								</strong>
								. As multas vão de{' '}
								<strong className="text-[#1c1c1c]">
									R$ 1.800 a R$ 6.700 por colaborador
								</strong>
								, com possibilidade de reincidência.
							</p>
						</div>
					</div>
				</section>

				<section className="bg-brand-cream px-6 py-16 md:px-10 md:py-[72px]">
					<div className="mx-auto max-w-[800px]">
						<div className="font-albert mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-pink">
							Por que isso vira um problema do RH
						</div>
						<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.12] tracking-[-0.015em] md:text-[42px]">
							<span className="block">A norma é da empresa.</span>
							<span className="block">
								<em className="font-libre text-brand-pink">
									O trabalho cai no RH.
								</em>
							</span>
						</h2>
						<p className="font-albert mb-11 max-w-[580px] text-base leading-[1.75] text-[#696969]">
							Quem implementa a NR-1 no dia a dia é o RH — que já
							está sobrecarregado e sem ferramenta para isso.
						</p>
						<div className="grid grid-cols-1 gap-3.5 md:grid-cols-3">
							{PAIN_CARDS.map((card) => (
								<div
									key={card.quote}
									className="md:px-5.5 rounded-xl border border-[#e4e4e0] bg-white px-5 py-6"
								>
									<p className="font-libre mb-2.5 text-[15px] font-bold italic leading-snug text-[#1c1c1c]">
										{card.quote}
									</p>
									<p className="font-albert text-[13.5px] leading-[1.7] text-[#696969]">
										{card.desc}
									</p>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="relative overflow-hidden bg-[#0c0c0c] px-6 py-20 text-center text-white md:px-10 md:py-[110px]">
					<div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[900px] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(circle,rgba(225,29,116,0.18)_0%,transparent_60%)]" />
					<div className="relative z-10 mx-auto max-w-[760px]">
						<div className="font-albert mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-[#ff8aaa]">
							A virada
						</div>
						<h2 className="font-libre mb-6 text-[34px] font-bold leading-[1.1] md:text-[60px]">
							<span className="block">
								A Plumaa faz isso pelo RH.
							</span>
							<span className="block">
								<em className="font-libre text-brand-pink">
									Tudo. Todo mês.
								</em>
							</span>
						</h2>
						<p className="font-albert mx-auto mb-8 max-w-[580px] text-[17px] leading-[1.75] text-white/65">
							Você cadastra a equipe uma vez. A partir daí, nosso
							sistema envia os formulários, analisa as respostas,
							gera o laudo médico e agenda as palestras. O RH só
							abre o dashboard.
						</p>
						<div className="flex flex-wrap justify-center gap-2.5">
							{[
								'Formulários automáticos',
								'Análise feita pela Plumaa',
								'Laudo assinado por médica',
								'Pronto para auditoria',
							].map((label) => (
								<span
									key={label}
									className="font-albert rounded-full border border-brand-pink/30 bg-brand-pink/15 px-4 py-1.5 text-[13px] font-medium text-[#ff8aaa]"
								>
									{label}
								</span>
							))}
						</div>
					</div>
				</section>

				<section className="bg-brand-cream px-6 py-16 md:px-10 md:py-[72px]">
					<div className="mx-auto max-w-[800px]">
						<div className="font-albert mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-pink">
							Como funciona
						</div>
						<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.12] tracking-[-0.015em] md:text-[42px]">
							Quatro passos.
						</h2>
						<p className="mb-12 max-w-[580px] text-base leading-[1.75] text-[#696969]">
							Veja exatamente o que acontece depois que sua
							empresa ativa o programa — sem caixas-pretas, sem
							etapas que dependem de iniciativa do RH.
						</p>

						<div className="space-y-0">
							{[
								{
									num: '01',
									freq: 'Uma vez · em até 24h',
									title: 'Sua empresa ativa o programa',
									desc: (
										<>
											Você nos manda a lista de
											colaboradores. Em até 24 horas, cada
											um recebe acesso à plataforma online
											— tudo no navegador, sem instalar
											nada. Junto com o setup, sua empresa
											recebe uma{' '}
											<strong className="text-[#1c1c1c]">
												assinatura médica oficial
											</strong>{' '}
											para anexar ao inventário NR-1 — uma
											vez por contrato, com respaldo
											técnico e legal. O RH não precisa
											disparar nada: a Plumaa cuida da
											comunicação inicial.
										</>
									),
									chip: 'Único passo do RH · 24h para ativar · assinatura médica entregue no setup',
									gif: null as string | null,
									foot: null as string | null,
								},
								{
									num: '02',
									freq: 'Todo mês · automático',
									title: 'Formulário científico para cada colaborador',
									desc: (
										<>
											A plataforma envia, todo mês, um
											formulário curto baseado em
											evidência científica para cada
											colaborador. Resposta direto no
											navegador, em 5 minutos. Quem não
											responde recebe lembrete automático.
										</>
									),
									chip: 'Disparos automáticos · sem ação do RH',
									gif: GIF_QUESTIONARIO,
									foot: 'Instrumentos COPSOQ II-Br e CBI — validados internacionalmente, aceitos pelo Ministério do Trabalho.',
								},
								{
									num: '03',
									freq: 'Todo mês · atualizado automaticamente',
									title: 'Dashboard analisado mês a mês',
									desc: (
										<>
											Você abre a plataforma online e vê:
											índice geral da empresa,{' '}
											<strong className="text-[#1c1c1c]">
												análise detalhada por setor
											</strong>
											, alertas de risco e evolução mês a
											mês — atualizado automaticamente
											conforme as respostas chegam. E na{' '}
											<strong className="text-[#1c1c1c]">
												call de entrega dos primeiros
												resultados
											</strong>
											, nosso time guia o RH por cada
											indicador do dashboard — você não
											decifra nada sozinho.
										</>
									),
									chip: 'Análise por setor · acompanhada por call de leitura com nosso time',
									gif: GIF_DASHBOARD,
									foot: null,
								},
								{
									num: '04',
									freq: 'Todo mês · 2 encontros',
									title: '2 palestras de intervenção, escolhidas pelos dados',
									desc: (
										<>
											Com base nos pontos críticos do mês,
											agendamos{' '}
											<strong className="text-[#1c1c1c]">
												2 palestras
											</strong>{' '}
											com nossos especialistas —
											distribuídas ao longo do mês. O RH
											só divulga o link interno. Cada
											palestra é documentada e rastreada
											para auditoria.
										</>
									),
									chip: '2 palestras/mês · rastreadas para NR-1',
									gif: null,
									foot: null,
								},
							].map((step, stepIndex, arr) => (
								<div
									key={step.num}
									className="md:gap-x-5.5 grid grid-cols-[40px_1fr] gap-x-3.5 md:grid-cols-[52px_1fr]"
								>
									<div className="flex flex-col items-center">
										<div className="font-albert z-[1] flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-pink text-sm font-bold text-white md:size-[52px] md:text-base">
											{step.num}
										</div>
										{stepIndex < arr.length - 1 && (
											<div
												className="my-1 min-h-8 w-0.5 flex-1 bg-gradient-to-b from-brand-pink to-brand-pink/10"
												aria-hidden
											/>
										)}
									</div>
									<div className="pb-11 pt-2.5">
										<div className="font-albert mb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-brand-pink">
											{step.freq}
										</div>
										<h3 className="font-libre mb-2 text-lg font-bold tracking-[-0.01em] text-[#1c1c1c]">
											{step.title}
										</h3>
										<p className="font-albert max-w-[540px] text-[14.5px] leading-[1.75] text-[#696969]">
											{step.desc}
										</p>
										{step.foot && (
											<p className="font-albert mt-2 text-[11px] italic text-[#696969]/70">
												{step.foot}
											</p>
										)}
										<span className="font-albert mt-3 inline-flex items-center gap-1.5 rounded-md border border-brand-pink/20 bg-[#fff1f4] px-3 py-1.5 text-xs font-medium text-[#c4194a]">
											{step.chip}
										</span>
										{step.gif && (
											<div className="mt-6 max-w-[720px] overflow-hidden rounded-[14px] border border-[#e4e4e0] bg-white shadow-[0_18px_48px_rgba(0,0,0,0.08)]">
												<div className="aspect-video w-full overflow-hidden">
													<img
														src={step.gif}
														alt=""
														className={`block h-full w-full object-cover ${
															step.num === '02'
																? 'scale-[1.03]'
																: ''
														}`}
													/>
												</div>
												{step.num === '02' && (
													<div className="border-t border-[#e4e4e0] bg-[#0c0c0c] px-[18px] py-4 text-white">
														<div className="font-albert mb-1 text-sm font-semibold">
															Veja o formulário
															sendo preenchido
														</div>
														<p className="font-albert text-[12.5px] leading-[1.55] text-white/55">
															Tudo pelo navegador,
															em qualquer
															dispositivo. Em
															poucos minutos o
															colaborador responde
															e some da fila do
															RH.
														</p>
													</div>
												)}
												{step.num === '03' && (
													<div className="border-t border-[#e4e4e0] bg-[#0c0c0c] px-[18px] py-4 text-white">
														<div className="font-albert mb-1 text-sm font-semibold">
															Tour pelo dashboard
															do RH
														</div>
														<p className="font-albert text-[12.5px] leading-[1.55] text-white/55">
															Índice geral,
															drill-down por
															setor, alertas de
															risco e evolução
															mensal — tudo no
															navegador, sem
															exportar planilha.
														</p>
													</div>
												)}
											</div>
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="bg-white px-6 py-16 md:px-10 md:py-[72px]">
					<div className="mx-auto max-w-[800px]">
						<div className="font-albert mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-pink">
							O que sua empresa recebe
						</div>
						<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.12] tracking-[-0.015em] md:text-[42px]">
							<span className="block">Seis entregas,</span>
							<span className="block">
								<em className="font-libre text-brand-pink">
									todas rastreáveis.
								</em>
							</span>
						</h2>
						<p className="font-albert mb-11 max-w-[580px] text-base leading-[1.75] text-[#696969]">
							Cinco delas chegam todo mês, automaticamente. Uma é
							entregue uma única vez, no setup. Nenhuma exige
							trabalho do RH.
						</p>
						<div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
							{DELIVERABLES.map((d) => (
								<div
									key={d.num}
									className="rounded-[14px] border border-[#e4e4e0] bg-white px-5 py-6 transition-transform hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(225,29,116,0.1)]"
								>
									<div className="font-libre mb-2 text-sm font-bold text-brand-pink">
										{d.num}
									</div>
									<div className="font-libre mb-1.5 text-[14.5px] font-bold text-[#1c1c1c]">
										{d.title}
									</div>
									<p className="font-albert text-[13px] leading-[1.65] text-[#696969]">
										{d.desc}
									</p>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="bg-[#0c0c0c] px-6 py-16 text-white md:px-10 md:py-[72px]">
					<div className="mx-auto max-w-[800px]">
						<div className="font-albert mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-[#ff8aaa]">
							Investimento
						</div>
						<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.12] tracking-[-0.015em] text-white md:text-[42px]">
							<span className="block">Quanto custa</span>
							<span className="block">
								<em className="font-libre text-brand-pink">
									proteger sua empresa.
								</em>
							</span>
						</h2>
						<p className="font-albert mb-11 max-w-[580px] text-base leading-[1.75] text-white/50">
							Um valor único por colaborador, do mês 1 ao mês 12.
							Sem teste grátis, sem desconto temporário, sem
							mensalidade que dobra. O que você simula abaixo é o
							que sai na fatura.
						</p>

						<div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 md:p-9">
							<div className="mb-8">
								<div className="mb-2.5 flex items-baseline justify-between gap-3">
									<span className="font-albert text-[11px] font-medium uppercase tracking-[0.05em] text-white/50">
										Número de colaboradores
									</span>
									<input
										type="number"
										min={1}
										max={6000}
										value={clampedCols}
										onChange={(e) => {
											const v = parseInt(
												e.target.value,
												10,
											);
											setCols(Number.isFinite(v) ? v : 1);
										}}
										className="font-libre w-[90px] border-0 border-b-2 border-brand-pink/50 bg-transparent text-right text-[28px] font-bold text-white outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
									/>
								</div>
								<input
									type="range"
									min={1}
									max={6000}
									value={clampedCols}
									onChange={(e) =>
										setCols(
											parseInt(e.target.value, 10) || 1,
										)
									}
									className="h-1.5 w-full cursor-pointer appearance-none rounded-sm bg-white/15 accent-brand-pink"
								/>
							</div>

							<div className="mb-7 grid grid-cols-2 gap-2 sm:grid-cols-4">
								{[
									{
										name: 'Micro',
										price: 'R$ 15,00',
										range: 'até 10',
									},
									{
										name: 'Pequeno',
										price: 'R$ 12,00',
										range: '11 – 30',
									},
									{
										name: 'Médio',
										price: 'R$ 8,00',
										range: '31 – 100',
									},
									{
										name: 'Grande',
										price: 'R$ 5,00',
										range: '100+',
									},
								].map((pill, idx) => (
									<div
										key={pill.name}
										className={`rounded-lg border px-2 py-2.5 text-center transition-colors ${
											tierIdx === idx
												? 'border-brand-pink/50 bg-brand-pink/15'
												: 'border-white/10 bg-white/5'
										}`}
									>
										<div className="font-albert mb-1 text-[10px] uppercase tracking-[0.08em] text-white/40">
											{pill.name}
										</div>
										<div
											className={`font-libre text-lg font-bold ${
												tierIdx === idx
													? 'text-[#ff8aaa]'
													: 'text-white'
											}`}
										>
											{pill.price}
										</div>
										<div className="font-albert mt-1 text-[10px] text-white/25">
											{pill.range}
										</div>
									</div>
								))}
							</div>

							<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
								<div className="rounded-[10px] border border-white/10 bg-white/5 px-5 py-6">
									<div className="font-albert mb-2 text-[11px] uppercase tracking-[0.05em] text-white/40">
										Valor por colaborador/mês
									</div>
									<div className="font-libre text-[30px] font-bold leading-none text-white">
										{brl(tier.price, true)}
									</div>
									<div className="font-albert mt-1.5 text-xs text-white/30">
										{tier.label} · {clampedCols}{' '}
										colaboradores
									</div>
								</div>
								<div className="rounded-[10px] border border-white/10 bg-white/5 px-5 py-6">
									<div className="font-albert mb-2 text-[11px] uppercase tracking-[0.05em] text-white/40">
										Investimento mensal
									</div>
									<div className="font-libre text-[30px] font-bold leading-none text-[#ff8aaa]">
										{brl(monthly)}
									</div>
									<div className="font-albert mt-1.5 text-xs text-white/30">
										{clampedCols} col ×{' '}
										{brl(tier.price, true)}/mês
									</div>
								</div>
								<div className="rounded-[10px] border border-brand-pink/30 bg-brand-pink/10 px-5 py-6 sm:col-span-2">
									<div className="font-albert mb-2 text-[11px] uppercase tracking-[0.05em] text-white/40">
										Investimento anual estimado
									</div>
									<div className="font-libre text-[30px] font-bold leading-none text-[#ff8aaa]">
										{brl(annual)}
									</div>
									<div className="font-albert mt-1.5 text-xs text-white/30">
										Valor cheio · sem rampa, sem pegadinha
									</div>
								</div>
							</div>

							<p className="font-albert mt-5 text-center text-xs tracking-wide text-white/40">
								A fatura começa no mês 1 com o mesmo valor que
								você vê acima. Sem cobrança escondida, sem
								reajuste após teste grátis.
							</p>
						</div>
					</div>
				</section>

				<section className="bg-brand-cream px-6 py-16 md:px-10 md:py-[72px]">
					<div className="mx-auto max-w-[800px]">
						<div className="font-albert mb-3.5 inline-block rounded-full border border-brand-pink/25 bg-[#fff1f4] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-[#c4194a]">
							Bônus para os colaboradores
						</div>
						<h2 className="font-libre mb-4 text-[26px] font-bold leading-[1.12] tracking-[-0.015em] md:text-[42px]">
							<span className="block">E ainda tem mais.</span>
							<span className="block">
								<em className="font-libre text-brand-pink">
									Cada colaborador entra no clube de saúde
									mental Plumaa.
								</em>
							</span>
						</h2>
						<p className="font-albert mb-10 max-w-[580px] text-base leading-[1.75] text-[#696969]">
							Além do programa institucional contratado pela
							empresa, todo colaborador da sua equipe entra
							automaticamente na nossa plataforma individual — com
							terapia online, médico online e nutricionista
							online, com descontos exclusivos. A empresa pode
							subsidiar ou deixar livre. O colaborador escolhe o
							plano.
						</p>

						<div className="mx-auto mb-10 max-w-[720px] overflow-hidden rounded-[14px] border border-[#e4e4e0] bg-white shadow-[0_18px_48px_rgba(0,0,0,0.08)]">
							<div className="aspect-video w-full overflow-hidden">
								<img
									src={GIF_AGENDAR}
									alt="Demonstração: agendamento de terapia online pelo colaborador"
									className="block h-full w-full scale-[1.03] object-cover"
								/>
							</div>
							<div className="border-t border-[#e4e4e0] bg-[#0c0c0c] px-[18px] py-4 text-white">
								<div className="font-albert mb-1 text-sm font-semibold">
									Como o colaborador agenda terapia
								</div>
								<p className="font-albert text-[12.5px] leading-[1.55] text-white/55">
									O bônus em ação: agendamento direto pela
									plataforma online, em poucos cliques.
								</p>
							</div>
						</div>

						<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
							{BONUS_PLANS.map((plan) => (
								<div
									key={plan.name}
									className={`flex flex-col rounded-[14px] border p-5 transition-transform hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(225,29,116,0.1)] ${
										plan.featured
											? 'border-brand-pink bg-[#fff1f4]'
											: 'border-[#e4e4e0] bg-white'
									}`}
								>
									<div className="font-libre mb-1.5 flex items-center gap-2 text-[17px] font-bold tracking-[-0.005em] text-[#1c1c1c]">
										<span
											className={`inline-block size-[11px] shrink-0 rounded-[3px] ${
												planDotClass[plan.dot]
											}`}
										/>
										{plan.name}
									</div>
									<p className="font-albert mb-[18px] min-h-14 text-[12.5px] leading-[1.55] text-[#696969]">
										{plan.tagline}
									</p>
									<ul
										className={`font-albert mb-[18px] flex-1 space-y-3 border-t pt-3.5 text-[12.5px] leading-[1.55] text-[#1c1c1c] ${
											plan.featured
												? 'border-brand-pink/20'
												: 'border-[#e4e4e0]'
										}`}
									>
										{plan.benefits.map((b) => (
											<li
												key={b}
												className="relative pl-5 before:absolute before:left-0 before:font-bold before:text-brand-pink before:content-['✓']"
											>
												{b}
											</li>
										))}
									</ul>
									<div
										className={`font-libre border-t pt-3.5 text-[22px] font-bold ${
											plan.featured
												? 'border-brand-pink/20 text-[#c4194a]'
												: 'border-[#e4e4e0] text-[#1c1c1c]'
										}`}
									>
										{plan.price}
										<span className="font-albert ml-1 text-xs font-normal text-[#696969]">
											/mês
										</span>
									</div>
								</div>
							))}
						</div>
						<p className="font-albert mt-3 text-xs text-[#696969]">
							* Os planos individuais são opcionais e custeados
							pelo próprio colaborador — ou pela empresa, conforme
							política de benefícios adotada.
						</p>
					</div>
				</section>

				<section className="relative overflow-hidden bg-brand-pink px-6 py-16 text-center text-white md:px-10 md:py-20">
					<div className="pointer-events-none absolute left-1/2 top-[-120px] h-[400px] w-[500px] -translate-x-1/2 bg-[radial-gradient(circle,rgba(255,255,255,0.15)_0%,transparent_65%)]" />
					<div className="relative z-10 mx-auto max-w-[460px]">
						<h2 className="font-libre mb-3.5 text-[28px] font-bold leading-tight md:text-[44px]">
							<span className="block">
								Pronto para tirar a NR-1
							</span>
							<span className="block">da mesa do RH?</span>
						</h2>
						<p className="font-albert mb-9 text-base leading-relaxed text-white/85">
							Em 24 horas sua empresa está cadastrada. No primeiro
							mês você já tem dashboard com dados reais.
						</p>
						<Button
							onClick={handleContact}
							className="font-albert mx-auto rounded-lg bg-white px-10 py-4 text-[15px] font-bold normal-case tracking-wide text-[#c4194a] shadow-none transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_14px_36px_rgba(0,0,0,0.18)]"
							text="Falar com a Plumaa"
						/>
						<p className="font-albert mt-6 text-xs text-white/55">
							Abril 2026 · plumaa.com.br
						</p>
					</div>
				</section>
			</main>

			<Footer />
		</section>
	);
};
